import { zodResolver } from "apps/web/src/utils/zodResolver";
import { Button, Dialog, DialogActions, DialogContent, Tooltip } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import React from "react";
import { useForm } from "react-hook-form";
import { useRefetchQueriesContext } from "../../../contexts/RefetchQueries";
import {
  useCreateBrandedFoodMutation,
  useCreateFoodServingsMutation,
  useCreateGenericFoodMutation,
  useEditBrandedFoodMutation,
  useEditFoodServingMutation,
  useEditGenericFoodMutation,
  useGramUnitQuery,
  useUnitsWithConversionsQuery,
} from "../../../types";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import LoadingBackdrop from "../../universal/LoadingBackdrop";
import { BaseFoodFormType, FoodFormSchema } from "../FoodFormSchema/FoodFormSchema";
import {
  toCreateBrandedFoodInput,
  toCreateFoodServingsInput,
  toCreateGenericFoodInput,
  toEditBrandedFoodInput,
  toEditFoodServingInputs,
  toEditGenericFoodInput,
} from "../FoodFormSchema/foodSchemaToInputUtils";
import { FoodForm } from "../Form/FoodForm";

interface FoodModalProps {
  open: boolean;
  onClose: () => void;
  initialFormState: Partial<BaseFoodFormType>;
  resetTable: () => void;
}

export const StaffFoodModal = ({ open, onClose, initialFormState, resetTable }: FoodModalProps) => {
  const refetchQueries = useRefetchQueriesContext();
  const { setMessage } = useSnackbar();

  const form = useForm<BaseFoodFormType>({
    mode: "onChange",
    defaultValues: initialFormState,
    resolver: zodResolver(FoodFormSchema()),
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitted, isValid },
  } = form;
  const watchedId = watch("id");
  const hasFormErrors = isSubmitted && !isValid;

  const onCompleted = () => {
    resetTable();
    setMessage("success", `Serving type saved successfully`);
  };

  const [createGenericFood] = useCreateGenericFoodMutation({
    refetchQueries,
    onCompleted(data) {
      resetTable();
      setMessage("success", `${data?.createGenericFood?.genericFood.name} has been added to staff foods`);
    },
    onError: e => setMessage("error", e.message),
  });
  const [editGenericFood] = useEditGenericFoodMutation({
    refetchQueries,
    onCompleted(data) {
      resetTable();
      setMessage("success", `${data?.editGenericFood?.genericFood.name} has been successfully updated`);
    },
    onError: e => setMessage("error", e.message),
  });
  const [createBrandedFood] = useCreateBrandedFoodMutation({
    refetchQueries,
    onCompleted(data) {
      resetTable();
      setMessage("success", `${data?.createBrandedFood?.brandedFood.name} has been added to staff foods`);
    },
    onError: e => setMessage("error", e.message),
  });
  const [editBrandedFood] = useEditBrandedFoodMutation({
    refetchQueries,
    onCompleted(data) {
      resetTable();
      setMessage("success", `${data?.editBrandedFood?.brandedFood.name} has been successfully updated`);
    },
    onError: e => setMessage("error", e.message),
  });
  const [createServings] = useCreateFoodServingsMutation({
    refetchQueries,
    onCompleted,
    onError: e => setMessage("error", e.message),
  });
  const [editServing] = useEditFoodServingMutation({
    refetchQueries,
    onCompleted,
    onError: e => setMessage("error", e.message),
  });

  const { data: unitsData } = useUnitsWithConversionsQuery();
  const { data: gramUnit } = useGramUnitQuery();

  const handleSave = async (state: BaseFoodFormType) => {
    const { id: editFoodId, type } = state;

    if (hasFormErrors) {
      // try re-triggering form to force validation
      await form.trigger();
      return;
    }

    if (!editFoodId) {
      if (type === "BrandedFood") {
        const input = toCreateBrandedFoodInput(state);
        if (!input) {
          return;
        }
        await createBrandedFood({
          variables: {
            input,
          },
        });
      } else {
        const input = toCreateGenericFoodInput(state);
        if (!input) {
          return;
        }
        await createGenericFood({
          variables: { input },
        });
      }
    } else {
      const editServingsInputs = toEditFoodServingInputs(state);
      const createServingsInput = toCreateFoodServingsInput(state);
      if (!editServingsInputs || !createServingsInput) {
        return;
      }

      let editFoodPromise: Promise<any>;
      if (type === "BrandedFood") {
        const editFoodInput = toEditBrandedFoodInput(state);
        if (!editFoodInput) {
          return;
        }
        editFoodPromise = editBrandedFood({
          variables: { input: editFoodInput },
        });
      } else {
        const editFoodInput = toEditGenericFoodInput(state);
        if (!editFoodInput) {
          return;
        }
        editFoodPromise = editGenericFood({
          variables: { input: editFoodInput },
        });
      }
      await Promise.all([
        editFoodPromise,
        createServings({
          variables: { input: createServingsInput },
        }),
        Promise.all(
          editServingsInputs.map(input =>
            editServing({
              variables: { input },
            })
          )
        ),
      ]);
    }
    onClose();
  };

  return unitsData && gramUnit ? (
    <Dialog
      maxWidth="xl"
      fullWidth
      open={open}
      onClose={onClose}>
      <DialogTitle title={`${!watchedId ? "Add" : "Edit"} Food`} onClose={onClose} />
      <DialogContent>
        <FoodForm
          form={form}
          unitsData={unitsData.units}
          gramUnit={gramUnit.gramUnit} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Tooltip title={hasFormErrors ? "Resolve the errors above to continue" : ""}>
          <Button onClick={handleSubmit(handleSave)}>Save</Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  ) : (
    <LoadingBackdrop open={open} onClose={onClose} />
  );
};
